import * as _ from 'lodash-es';
import React, { useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import { MenuItem } from '../../components/Menu';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { TopicPostsOrder } from '../../graphql/generated/graphql';
import arrowDownIcon from '../../icons/non-prefilled/16x16/arrowDown.svg';
import pinIcon from '../../icons/non-prefilled/16x16/pinOutline.svg';
import unpinIcon from '../../icons/non-prefilled/16x16/unpinOutline.svg';
import PostRow from '../../post/PostRow';
import PostRowMeta from '../../post/PostRowMeta';
import {
  PostPolicyAction,
  usePostPolicyCheck,
} from '../../post/postPolicyCheck';
import TopicPostOverflowMenuContent from './TopicPostOverflowMenuContent';
import * as styles from './TopicPostSection.module.scss';
import TopicPostsEmptyState from './TopicPostsEmptyState';
import TopicReorderablePosts from './TopicReorderablePosts';

const TOPIC_POST_SECTION_POST_FRAGMENT = gql(`
  fragment TopicPostSection_post on Post {
    id
    views
    userViewedAt
    updatedAt
    ...PostRow_post
    ...TopicPostOverflowMenuContent_post
    ...usePostPolicyCheck_post
  }
`);

const TOPIC_POST_SECTION_FRAGMENT = gql(`
  fragment TopicPostSection_posts on TopicPostConnection {
    edges {
      ...TopicReorderablePosts_postEdges
      node {
        id
        ...TopicPostSection_post
      }
      pinnedOrder
      cursor
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
`);

const METADATA_ATTRIBUTE = {
  [TopicPostsOrder.Popularity]: 'popularity',
  [TopicPostsOrder.PublishedAt]: 'publishedAt',
  [TopicPostsOrder.Title]: 'updatedAt',
  [TopicPostsOrder.UpdatedAt]: 'updatedAt',
  [TopicPostsOrder.ViewedAt]: 'userViewedAt',
} as const;

interface TopicPostSectionProps {
  header: React.ReactNode;
  fetchMore?: (() => void) | null;
  refetch: () => void;
  orderBy: TopicPostsOrder;
  pinPost?: ((id: string, order: number) => Promise<unknown>) | null;
  unpinPost?: ((id: string) => void) | null;
  topicId: string;
  posts?: DocumentType<typeof TOPIC_POST_SECTION_FRAGMENT> | null;
  main?: boolean;
  loading: boolean;
}

const TopicPostSection = ({
  header,
  fetchMore,
  refetch,
  pinPost,
  unpinPost,
  orderBy,
  posts,
  main,
  topicId,
  loading,
}: TopicPostSectionProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);

  const edges = _.compact(posts?.edges ?? []);

  const renderMeta = (
    post: DocumentType<typeof TOPIC_POST_SECTION_POST_FRAGMENT>,
  ) => (
    <PostRowMeta
      attribute={METADATA_ATTRIBUTE[orderBy]}
      values={{
        publishedAt: post.publishedAt,
        updatedAt: post.updatedAt,
        userViewedAt: post.userViewedAt,
        popularity: post.views,
      }}
    />
  );

  const reorderableEdges = pinPost
    ? edges.filter((e) => e.pinnedOrder != null)
    : [];

  const maxPin = Math.max(
    -1,
    ...reorderableEdges.map(({ pinnedOrder }) => pinnedOrder ?? -1),
  );

  const staticEdges = _.compact(
    pinPost ? edges.filter((e) => e.pinnedOrder == null) : (edges ?? []),
  );

  return (
    <div>
      {header}
      {!loading && posts && (
        <>
          {edges.length === 0 && main && (
            <TopicPostsEmptyState topicId={topicId} />
          )}
          {edges.length > 0 && (
            <PostRow.Provider>
              <div className={styles.posts}>
                {pinPost && reorderableEdges.length > 0 && (
                  <TopicReorderablePosts
                    orderPost={pinPost}
                    postEdges={reorderableEdges}
                    renderPostEdge={({ node: post }) => {
                      if (post == null) return <>{null}</>;

                      const canEditPost = postPolicyCheck(
                        PostPolicyAction.EditPost,
                        post,
                        currentUser,
                      );

                      return (
                        <PostRow
                          seriesHoverCardEnabled
                          meta={renderMeta(post)}
                          renderOverflowMenu={() => (
                            <TopicPostOverflowMenuContent
                              post={post}
                              canEditPost={canEditPost}
                              extraMenuItems={
                                <>
                                  {unpinPost && (
                                    <MenuItem
                                      icon={unpinIcon}
                                      onClick={() => {
                                        unpinPost(post.id);
                                      }}
                                    >
                                      Unpin
                                    </MenuItem>
                                  )}
                                </>
                              }
                              refetch={refetch}
                            />
                          )}
                          pinned
                          post={post}
                        />
                      );
                    }}
                  />
                )}
                {staticEdges.map(({ node: post, pinnedOrder }) => {
                  if (post == null) return null;

                  const canEditPost = postPolicyCheck(
                    PostPolicyAction.EditPost,
                    post,
                    currentUser,
                  );

                  return (
                    <PostRow
                      seriesHoverCardEnabled
                      meta={renderMeta(post)}
                      key={post.id}
                      renderOverflowMenu={() => (
                        <TopicPostOverflowMenuContent
                          post={post}
                          canEditPost={canEditPost}
                          extraMenuItems={
                            <>
                              {pinPost && (
                                <MenuItem
                                  icon={pinIcon}
                                  onClick={() => {
                                    pinPost(post.id, maxPin + 1);
                                  }}
                                >
                                  Pin
                                </MenuItem>
                              )}
                            </>
                          }
                          refetch={refetch}
                        />
                      )}
                      pinned={main ? pinnedOrder != null : undefined}
                      post={post}
                    />
                  );
                })}
                {fetchMore &&
                  (main ? (
                    <InfiniteScrollWaypoint fetchMore={fetchMore} />
                  ) : (
                    <ShowMoreButton onClick={() => fetchMore()} />
                  ))}
              </div>
            </PostRow.Provider>
          )}
        </>
      )}
    </div>
  );
};

const TOPIC_POST_SECTION_SIMPLE_LIST_POST_FRAGMENT = gql(`
  fragment TopicPostSectionSimpleList_post on Post {
    id
    ...PostRow_post
    ...TopicPostOverflowMenuContent_post
    ...usePostPolicyCheck_post
  }
`);

interface TopicPostSectionSimpleListProps<
  Post extends DocumentType<
    typeof TOPIC_POST_SECTION_SIMPLE_LIST_POST_FRAGMENT
  >,
> {
  header: React.ReactNode;
  posts: Post[];
  renderMeta: (post: Post) => React.ReactNode;
  fetchMore?: (() => Promise<unknown>) | null;
  fetchMoreType?: 'infinite-scroll' | 'button';
}

export const TopicPostSectionSimpleList = <
  Post extends DocumentType<
    typeof TOPIC_POST_SECTION_SIMPLE_LIST_POST_FRAGMENT
  >,
>({
  header,
  posts,
  renderMeta,
  fetchMore,
  fetchMoreType,
}: TopicPostSectionSimpleListProps<Post>) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  const fetchMoreElement =
    fetchMore && fetchMoreType === 'button' ? (
      <ShowMoreButton onClick={() => fetchMore()} />
    ) : (
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    );

  return (
    <div>
      {header}
      {posts.length > 0 && (
        <PostRow.Provider>
          <div className={styles.simplePosts}>
            {posts.map((post) => {
              const canEditPost = postPolicyCheck(
                PostPolicyAction.EditPost,
                post,
                currentUser,
              );
              return (
                <PostRow
                  meta={renderMeta(post)}
                  key={post.id}
                  renderOverflowMenu={() => (
                    <TopicPostOverflowMenuContent
                      post={post}
                      canEditPost={canEditPost}
                    />
                  )}
                  post={post}
                />
              );
            })}
            {fetchMoreElement}
          </div>
        </PostRow.Provider>
      )}
      {posts.length === 0 && fetchMoreElement}
    </div>
  );
};

interface ShowMoreButtonProps {
  onClick?: () => Promise<unknown> | void;
}

const ShowMoreButton = ({ onClick = () => {} }: ShowMoreButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    const promise = onClick();

    if (promise?.finally) {
      promise.finally(() => setIsLoading(false));
      setIsLoading(true);
    }
  };

  return (
    <Button
      variant="link"
      className={styles.showMoreButton}
      onClick={handleClick}
    >
      {isLoading ? (
        <LoadingIndicator delay={0} />
      ) : (
        <>
          <SVGIcon inline>{arrowDownIcon}</SVGIcon>
          <span className={styles.buttonText}>Show more</span>
        </>
      )}
    </Button>
  );
};

export default TopicPostSection;
